/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { Alert, Box, Divider } from "@mui/material";
import { PanelErrorBoundary } from "components/common/error/PanelErrorBoundary";
import { useScrapEyeMetadata } from "src/contexts/scrapEyeMetadata";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import { useMemo } from "react";
import { useTenantTranslation } from "hooks/formatters";
import {
  ScrapEyesEventWithReviewData,
  useManualReviewEvents,
} from "../contexts/ManualReviewEventContext";

dayjs.extend(utc);
import { findLabelValue } from "../utils/formatters";
import {
  findPairedEventForMassWindow,
  isMassEventOutsideWindow,
  isMassEventType,
} from "../utils/massEventWindow";
import { MassAdjustmentSection } from "./sections/MassAdjustmentSection";
import { MassChangeValues } from "./MassAdjustmentChart";
import { TimeAxisLayout } from "./timeAxisLayout";

export interface EditEventMassAdjustmentProps {
  mergedEvent: ScrapEyesEventWithReviewData;
  savedArea: string | null;
  playheadTime?: Dayjs;
  onMassChange: (values: MassChangeValues) => void;
  onAutoSync?: (values: MassChangeValues) => void;
  onTimeAxisChange?: (timeAxis: TimeAxisLayout | null) => void;
  isMassDetached: boolean;
  onToggleMassDetached: () => void;
}

export const EditEventMassAdjustment = ({
  mergedEvent,
  savedArea,
  playheadTime,
  onMassChange,
  onAutoSync,
  onTimeAxisChange,
  isMassDetached,
  onToggleMassDetached,
}: EditEventMassAdjustmentProps) => {
  const {
    extra_data: extraData,
    mass_area_matching: massAreaMatching,
    mass_event_matching: massEventMatching,
  } = useScrapEyeMetadata();
  const { allEvents, windowStart, windowEnd } = useManualReviewEvents();
  const { t } = useTenantTranslation();
  const hasScaleWeights = extraData?.includes("mass_data") ?? false;

  const pairedEvent = useMemo(
    () =>
      findPairedEventForMassWindow({
        event: mergedEvent,
        allEvents,
        massEventMatching,
      }),
    [mergedEvent, allEvents, massEventMatching]
  );

  const matchedMassEvent = mergedEvent.mass_event;
  const showMatchedMassEvent = matchedMassEvent !== null && !isMassDetached;

  const massEventStart = showMatchedMassEvent
    ? dayjs.utc(matchedMassEvent.event.start)
    : null;
  const massEventEnd = showMatchedMassEvent
    ? dayjs.utc(matchedMassEvent.event.end)
    : null;
  const massEventMass = showMatchedMassEvent
    ? (findLabelValue(matchedMassEvent.labels, "mass") ?? null)
    : null;
  const eventMass = isMassDetached ? null : (mergedEvent.mass ?? massEventMass);
  // Stored levels were read off the saved area's scale. After an unsaved area
  // change the chart re-reads them from the new area's trace instead.
  const showStoredLevels = !isMassDetached && mergedEvent.area === savedArea;

  const hasMassEventOutsideWindow =
    showMatchedMassEvent &&
    isMassEventOutsideWindow(matchedMassEvent, windowStart, windowEnd);

  const showMassAdjustment =
    isMassEventType(mergedEvent.eventType, massEventMatching) &&
    hasScaleWeights;

  if (!showMassAdjustment) {
    return null;
  }

  return (
    <>
      <Divider />
      <Box sx={{ p: 2 }}>
        {hasMassEventOutsideWindow ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t("massEventOutsideWindow")}
          </Alert>
        ) : null}
        <PanelErrorBoundary panel="mass-adjustment-chart">
          <MassAdjustmentSection
            eventId={mergedEvent.id}
            eventStart={mergedEvent.start}
            eventEnd={mergedEvent.end}
            massEventStart={massEventStart}
            massEventEnd={massEventEnd}
            eventMass={eventMass}
            massEventMass={massEventMass}
            area={massAreaMatching ? mergedEvent.area : null}
            storedInitialMass={
              showStoredLevels ? (mergedEvent.initialMass ?? null) : null
            }
            storedFinalMass={
              showStoredLevels ? (mergedEvent.finalMass ?? null) : null
            }
            pairedEventStart={pairedEvent?.start ?? null}
            pairedEventEnd={pairedEvent?.end ?? null}
            playheadTime={playheadTime}
            onMassChange={onMassChange}
            onAutoSync={onAutoSync}
            onTimeAxisChange={onTimeAxisChange}
            canDetachMassEvent={
              matchedMassEvent !== null || mergedEvent.mass != null
            }
            isMassDetached={isMassDetached}
            onToggleMassDetached={onToggleMassDetached}
          />
        </PanelErrorBoundary>
      </Box>
    </>
  );
};
